const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api/v1";

/** Public API host that tokens are used against (the console itself calls API_BASE). */
export const PUBLIC_API_URL = "https://api.nilssonhedge.com";

/** NilssonHedge's page that signs a subscriber in here (the NH handoff), with their plan. */
export const NH_SIGN_IN_URL = "https://nilssonhedge.com/nh-api-access/";

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const resp = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
    },
    ...init,
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new ApiError(resp.status, text || resp.statusText);
  }
  if (resp.status === 204) return undefined as T;
  return resp.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: "GET" }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "POST",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "PATCH",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  delete: <T = void>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "DELETE",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  /** Downloads a CSV export. Returns the continuation offset if the server truncated it, else null. */
  download: async (path: string, filename: string): Promise<number | null> => {
    const resp = await fetch(`${API_BASE}${path}`, { credentials: "include" });
    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      throw new ApiError(resp.status, text || resp.statusText);
    }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    const next = resp.headers.get("X-Next-Offset");
    return next ? Number(next) : null;
  },
};

/** A plan's limits as the API enforces them (GET /plans, and the tier on /users/me). */
export interface PlanInfo {
  name: string;
  display_name: string;
  fund_scope: string;
  daily_indices: boolean;
  history_years: number | null;
  max_funds: number | null;
  max_results_per_page: number;
  can_bulk_export: boolean;
  requests_per_minute: number;
  requests_per_hour: number;
  requests_per_day: number;
}

export interface UserMe {
  id: string;
  email: string;
  name: string | null;
  is_active: boolean;
  external_provider: string | null;
  roles: string[];
  permissions: string[];
  tier: PlanInfo;
  funds_used: number | null;
}

export interface TokenInfo {
  id: string;
  name: string;
  is_active: boolean;
  expires_at: string;
  last_used_at: string | null;
  created_at: string;
  revoked_at: string | null;
}

export interface TokenCreated {
  token: string;
  info: TokenInfo;
}

// ── Watchlist types ─────────────────────────────────────────

export interface WatchlistSummary {
  id: string;
  name: string;
  item_count: number;
  created_at: string;
  updated_at: string;
}

export interface WatchlistItemEntry {
  manager_id: number;
  program_id: number;
  manager: string | null;
  program: string | null;
}

export interface WatchlistDetail {
  id: string;
  name: string;
  items: WatchlistItemEntry[];
  created_at: string;
  updated_at: string;
}

export interface WatchlistAddResult {
  added: number;
  skipped: number;
  rejected: number;
}

export interface FundSearchItem {
  manager_id: number;
  program_id: number;
  manager: string | null;
  program: string | null;
  country: string | null;
  region: string | null;
  closed: boolean | null;
  has_returns: boolean;
}

export interface FundSearchResponse {
  items: FundSearchItem[];
  total: number;
}

// ── Index types ─────────────────────────────────────────────

export interface IndexSummary {
  type: string;
  frequencies: string[];
  first_date: string;
  last_date: string;
  count: number;
}

export interface IndexPoint {
  type: string;
  date: string;
  frequency: string;
  ror: string | null;
  cnt: number | null;
  aum_bn: string | null;
}

export interface IndexSeriesResponse {
  items: IndexPoint[];
  truncated: boolean;
  limit: number;
}

export interface ExportFilters {
  country: string[];
  region: string[];
  strategy: string[];
  style: string[];
  sector: string[];
}

// ── Admin types ──────────────────────────────────────────────

export interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  is_active: boolean;
  email_verified: boolean;
  external_provider: string | null;
  roles: string[];
  features: string[];
  tier_name: string | null;
  our_tier_name: string | null;
  our_tier_expires_at: string | null;
  nh_sync_due: boolean;
  created_at: string;
}

export interface AdminTier {
  id: string;
  name: string;
  display_name: string;
  description: string | null;
  priority: number;
  requests_per_minute: number;
  requests_per_hour: number;
  requests_per_day: number;
  fund_scope: string;
  history_years: number | null;
  max_funds: number | null;
  max_results_per_page: number;
  bulk_export_limit: number;
  can_bulk_export: boolean;
  is_active: boolean;
}

export interface CuratedFundEntry {
  manager_id: number;
  program_id: number;
}

export interface CuratedFundList {
  items: CuratedFundEntry[];
  total: number;
}
export interface AdminUsage {
  days: number;
  total: number;
  unique_users: number;
  error_count: number;
  by_day: { day: string; count: number }[];
  by_route: { method: string; path: string; count: number; avg_ms: number; error_count: number }[];
  by_status: { status: number; count: number }[];
  by_user: { user_id: string | null; email: string | null; count: number; last_seen: string }[];
  by_token: { token_id: string; name: string | null; count: number; last_seen: string }[];
}

// ── Usage types (current user) ─────────────────────────────

export interface MyUsage {
  days: number;
  total: number;
  error_count: number;
  by_day: { day: string; count: number }[];
  by_route: { method: string; path: string; count: number; avg_ms: number; error_count: number }[];
  by_status: { status: number; count: number }[];
  by_token: { token_id: string; name: string | null; count: number; last_seen: string }[];
}

export interface UsageRequest {
  id: number;
  method: string;
  path: string;
  query: string | null;
  status: number;
  duration_ms: number;
  token_name: string | null;
  email?: string | null;
  created_at: string;
}

export interface UsageRequestPage {
  items: UsageRequest[];
  has_more: boolean;
}
